/**
 * Local Component Service
 *
 * Unified service for managing local AI components.
 * Bridges Electron IPC (for desktop) and API (for web) to provide
 * a consistent interface for component management.
 */

import * as componentService from './componentService';
import { isElectronMode } from '../utils/apiMode';

import { handleError } from '../utils/errorHandler';

// ============================================================================
// Types
// ============================================================================

export interface ComponentLocalStatus {
  componentId: string;
  installed: boolean;
  running: boolean;
  path: string | null;
  version: string | null;
  isElectron: boolean;
  canInstallLocally: boolean;
  hasCloudAlternative: boolean;
  error?: string;
}

export interface InstallProgress {
  componentId: string;
  step:
    | 'starting'
    | 'downloading'
    | 'extracting'
    | 'installing'
    | 'configuring'
    | 'complete'
    | 'error';
  progress: number; // 0-100
  message: string;
  error?: string;
}

export interface PathSelectionResult {
  path: string;
  valid: boolean;
  error?: string;
}

export interface ComponentAction {
  success: boolean;
  error?: string;
}

// ============================================================================
// Cloud Alternatives Mapping
// ============================================================================

/**
 * Maps components to their cloud alternatives
 */
export const CLOUD_ALTERNATIVES: Record<
  string,
  { available: boolean; provider: string; requires?: string[] }
> = {
  ollama: { available: true, provider: 'OpenRouter', requires: ['OPENROUTER_API_KEY'] },
  fooocus: { available: true, provider: 'RunPod', requires: ['FOOOCUS_ENDPOINT_ID'] },
  whisperx: { available: true, provider: 'Cloud Run API' },
  nllb: { available: true, provider: 'Cloud Run API' },
  kokoro_tts: { available: true, provider: 'Cloud Run API' },
  piper_tts: { available: true, provider: 'Cloud Run API' },
};

/**
 * Component display names and descriptions
 */
export const COMPONENT_INFO: Record<
  string,
  { name: string; description: string; requirements?: string }
> = {
  ollama: {
    name: 'Ollama LLM',
    description: 'Run language models locally',
    requirements: '8GB RAM, 5GB disk',
  },
  fooocus: {
    name: 'Fooocus AI',
    description: 'Generate images with Stable Diffusion XL',
    requirements: '~15GB download, NVIDIA GPU 8GB+ VRAM',
  },
  whisperx: {
    name: 'WhisperX',
    description: 'Transcribe audio and generate subtitles',
    requirements: '3GB download, GPU recommended',
  },
  nllb: {
    name: 'NLLB Translation',
    description: 'Translate between 200+ languages',
    requirements: '2GB download, 4GB RAM',
  },
  kokoro_tts: {
    name: 'Kokoro TTS',
    description: 'High-quality text-to-speech (GPU)',
    requirements: '1GB download, NVIDIA GPU 2GB+ VRAM',
  },
  piper_tts: {
    name: 'Piper TTS',
    description: 'Lightweight text-to-speech (CPU)',
    requirements: '500MB download, 2GB RAM',
  },
  comfyui_gguf: {
    name: 'ComfyUI GGUF',
    description:
      'ComfyUI custom node for running GGUF-quantized diffusion models (required by Wan local providers)',
    requirements: '~50 MB, ComfyUI must be installed first',
  },
  wan21_1_3b: {
    name: 'Wan 2.1 1.3B',
    description: 'Lightweight local T2V model — 480p output, fast generation',
    requirements: '~1.1 GB download, 8 GB VRAM minimum',
  },
  wan22_5b: {
    name: 'Wan 2.2 5B (TI2V)',
    description: 'Versatile local T2V and I2V model — up to 720p output',
    requirements: '~3 GB download, 12 GB VRAM minimum',
  },
  wan22_14b: {
    name: 'Wan 2.2 14B (SOTA)',
    description: 'Premium state-of-the-art local T2V with LightX2V Lightning LoRA',
    requirements: '~20 GB download, 20 GB VRAM minimum (RTX 4090 24 GB+)',
  },
};

// ============================================================================
// Electron API Type
// ============================================================================

interface ElectronComponentAPI {
  componentGetStatus: (componentId: string) => Promise<ComponentLocalStatus>;
  componentSelectPath: (componentId: string) => Promise<PathSelectionResult | null>;
  componentStart: (componentId: string) => Promise<ComponentAction>;
  componentStop: (componentId: string) => Promise<ComponentAction>;
  componentInstall: (
    componentId: string,
    options?: Record<string, unknown>
  ) => Promise<ComponentAction>;
  componentGetPath: (componentId: string) => Promise<string>;
  componentSetPath: (componentId: string, path: string) => Promise<boolean>;
  componentGetSettings: (componentId: string) => Promise<Record<string, unknown>>;
  componentSetSettings: (
    componentId: string,
    settings: Record<string, unknown>
  ) => Promise<boolean>;
  onComponentInstallProgress: (callback: (progress: InstallProgress) => void) => () => void;
  isElectron: boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if running in Electron environment.
 * Delegates to centralized apiMode utility for consistent behavior.
 */
export function isElectronEnvironment(): boolean {
  return isElectronMode();
}

/**
 * Get the Electron API if available
 */
function getElectronAPI(): ElectronComponentAPI | null {
  if (isElectronEnvironment()) {
    return (window as Window & { electronAPI?: ElectronComponentAPI }).electronAPI || null;
  }
  return null;
}

/**
 * Build the localStorage key for a component's persisted settings.
 * Kept as a single source of truth so the key value stays backward-compatible
 * with existing users' localStorage (do NOT change the produced string).
 */
const componentSettingsKey = (componentId: string): string => `component-settings-${componentId}`;

/**
 * Run a component lifecycle action (start/stop) across the Electron-IPC and
 * web-API code paths with identical error handling.
 *
 * In Electron the IPC call already returns a ComponentAction; in web mode the
 * componentService resolves with a status payload that we ignore on success,
 * wrapping it in { success: true }.
 */
async function runComponentAction(
  electronCall: (api: ElectronComponentAPI) => Promise<ComponentAction>,
  webCall: () => Promise<unknown>,
  errorLabel: string
): Promise<ComponentAction> {
  const electronAPI = getElectronAPI();
  if (electronAPI) {
    try {
      return await electronCall(electronAPI);
    } catch (error) {
      console.error(`[LocalComponentService] Error ${errorLabel}:`, error);
      return {
        success: false,
        error: handleError(error, 'LocalComponentService', 'Unknown error'),
      };
    }
  }
  try {
    await webCall();
    return { success: true };
  } catch (error) {
    console.error(`[LocalComponentService] API error ${errorLabel}:`, error);
    return {
      success: false,
      error: handleError(error, 'LocalComponentService', 'Unknown error'),
    };
  }
}

// ============================================================================
// Main Service Functions
// ============================================================================

/**
 * Get component status
 * In Electron: Uses IPC for local status + API for running status
 * In Web: Uses API only
 */
export async function getComponentStatus(componentId: string): Promise<ComponentLocalStatus> {
  const electronAPI = getElectronAPI();
  const cloudAlt = CLOUD_ALTERNATIVES[componentId];

  // Electron: Use IPC for local status
  if (electronAPI) {
    try {
      const status = await electronAPI.componentGetStatus(componentId);
      return {
        ...status,
        hasCloudAlternative: cloudAlt?.available || false,
      };
    } catch (error) {
      console.error(`[LocalComponentService] Error getting status for ${componentId}:`, error);
      return {
        componentId,
        installed: false,
        running: false,
        path: null,
        version: null,
        isElectron: true,
        canInstallLocally: true,
        hasCloudAlternative: cloudAlt?.available || false,
        error: handleError(error, 'LocalComponentService', 'Unknown error'),
      };
    }
  }

  // Web: Use API
  try {
    const component = await componentService.getComponent(componentId);
    return {
      componentId,
      installed: component.status !== 'not_installed',
      running: component.status === 'running',
      path: null, // Not available in web mode
      version: component.version,
      isElectron: false,
      canInstallLocally: false, // Can't install locally in web mode
      hasCloudAlternative: cloudAlt?.available || false,
    };
  } catch (error) {
    console.error(`[LocalComponentService] API error for ${componentId}:`, error);
    return {
      componentId,
      installed: false,
      running: false,
      path: null,
      version: null,
      isElectron: false,
      canInstallLocally: false,
      hasCloudAlternative: cloudAlt?.available || false,
      error: handleError(error, 'LocalComponentService', 'Unknown error'),
    };
  }
}

/**
 * Select custom installation path for a component
 * Only available in Electron
 */
export async function selectComponentPath(
  componentId: string
): Promise<PathSelectionResult | null> {
  const electronAPI = getElectronAPI();

  if (!electronAPI) {
    console.warn('[LocalComponentService] selectComponentPath is only available in Electron');
    return null;
  }

  try {
    return await electronAPI.componentSelectPath(componentId);
  } catch (error) {
    console.error(`[LocalComponentService] Error selecting path for ${componentId}:`, error);
    return {
      path: '',
      valid: false,
      error: handleError(error, 'LocalComponentService', 'Unknown error'),
    };
  }
}

/**
 * Start a component
 * In Electron: Uses IPC
 * In Web: Uses API
 */
export async function startComponent(componentId: string): Promise<ComponentAction> {
  return runComponentAction(
    (api) => api.componentStart(componentId),
    () => componentService.startComponent(componentId),
    `starting ${componentId}`
  );
}

/**
 * Stop a component
 * In Electron: Uses IPC
 * In Web: Uses API
 */
export async function stopComponent(componentId: string): Promise<ComponentAction> {
  return runComponentAction(
    (api) => api.componentStop(componentId),
    () => componentService.stopComponent(componentId),
    `stopping ${componentId}`
  );
}

/**
 * Install a component locally
 * Only available in Electron
 */
export async function installComponent(
  componentId: string,
  options?: { path?: string; skipModels?: boolean },
  onProgress?: (progress: InstallProgress) => void
): Promise<ComponentAction> {
  const electronAPI = getElectronAPI();

  if (!electronAPI) {
    // In web mode, try to use the API
    try {
      await componentService.installComponent(componentId);
      return { success: true };
    } catch (error) {
      console.error(`[LocalComponentService] API error installing ${componentId}:`, error);
      return {
        success: false,
        error: handleError(error, 'LocalComponentService', 'Unknown error'),
      };
    }
  }

  // Set up progress listener if callback provided
  let unsubscribe: (() => void) | null = null;
  if (onProgress) {
    unsubscribe = electronAPI.onComponentInstallProgress((progress) => {
      if (progress.componentId === componentId) {
        onProgress(progress);
      }
    });
  }

  try {
    const result = await electronAPI.componentInstall(componentId, options);
    return result;
  } catch (error) {
    console.error(`[LocalComponentService] Error installing ${componentId}:`, error);
    return { success: false, error: handleError(error, 'LocalComponentService', 'Unknown error') };
  } finally {
    if (unsubscribe) {
      unsubscribe();
    }
  }
}

/**
 * Get component settings.
 * In Electron: Uses IPC.
 * In Web: Uses localStorage.
 *
 * Returns an empty object when no settings have been saved yet.
 * Throws when the read itself fails (IPC error or JSON parse error)
 * so callers can distinguish "user has no settings" from "read failed".
 * A caller that silently falls through to defaults on failure risks
 * overwriting real settings on a subsequent save.
 */
export async function getComponentSettings(componentId: string): Promise<Record<string, unknown>> {
  const electronAPI = getElectronAPI();

  if (electronAPI) {
    // Propagate IPC errors — callers must not silently save over real settings.
    return await electronAPI.componentGetSettings(componentId);
  }

  // Web: Use localStorage.
  // Return {} only when there genuinely are no saved settings (key absent).
  // Re-throw on JSON parse errors so callers know the stored value is corrupt.
  const stored = localStorage.getItem(componentSettingsKey(componentId));
  if (stored === null) {
    return {};
  }
  return JSON.parse(stored) as Record<string, unknown>;
}

/**
 * Update component settings
 * In Electron: Uses IPC
 * In Web: Uses localStorage
 */
export async function updateComponentSettings(
  componentId: string,
  settings: Record<string, unknown>
): Promise<boolean> {
  const electronAPI = getElectronAPI();

  if (electronAPI) {
    try {
      return await electronAPI.componentSetSettings(componentId, settings);
    } catch (error) {
      console.error(`[LocalComponentService] Error updating settings for ${componentId}:`, error);
      return false;
    }
  }

  // Web: Use localStorage
  try {
    const current = await getComponentSettings(componentId);
    localStorage.setItem(
      componentSettingsKey(componentId),
      JSON.stringify({ ...current, ...settings })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Subscribe to install progress events
 * Only available in Electron
 */
export function subscribeToInstallProgress(
  componentId: string,
  onProgress: (progress: InstallProgress) => void
): () => void {
  const electronAPI = getElectronAPI();

  if (!electronAPI) {
    // In web mode, use SSE from the API
    return componentService.subscribeToInstallProgress(
      componentId,
      (progress) => {
        onProgress({
          componentId,
          step: progress.step as InstallProgress['step'],
          progress: progress.progress,
          message: progress.message,
        });
      },
      () => {},
      (error) => {
        onProgress({
          componentId,
          step: 'error',
          progress: 0,
          message: error.message,
          error: error.message,
        });
      }
    );
  }

  // Electron: Use IPC events
  return electronAPI.onComponentInstallProgress((progress) => {
    if (progress.componentId === componentId) {
      onProgress(progress);
    }
  });
}

/**
 * Get error code to component ID mapping
 */
export function getComponentIdFromErrorCode(errorCode?: string): string | null {
  if (!errorCode) return null;

  const prefix = errorCode.split('_')[0];
  const mapping: Record<string, string> = {
    FOOOCUS: 'fooocus',
    OLLAMA: 'ollama',
    WHISPER: 'whisperx',
    WHISPERX: 'whisperx',
    NLLB: 'nllb',
    KOKORO: 'kokoro_tts',
    PIPER: 'piper_tts',
  };

  return mapping[prefix] || null;
}

/**
 * Check if an error code indicates a configuration/setup issue
 */
export function isConfigurationError(errorCode?: string): boolean {
  if (!errorCode) return false;

  const configErrorPatterns = [
    'NOT_RUNNING',
    'NOT_INSTALLED',
    'NOT_CONFIGURED',
    'MISSING_API_KEY',
    'MISSING_ENDPOINT',
    'NOT_AVAILABLE',
    'NOT_LOADED',
  ];

  return configErrorPatterns.some((pattern) => errorCode.includes(pattern));
}
